import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Button,
  Form,
  Spinner,
  Alert,
  InputGroup,
  Card,
} from 'react-bootstrap';
import {
  FaSearch,
  FaArrowLeft,
  FaChartLine,
  FaCalendarAlt,
  FaRupeeSign,
  FaArrowUp,
  FaArrowDown,
} from 'react-icons/fa';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';

const ProfitLoss = () => {
  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [profitLoss, setProfitLoss] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });

  const fetchProfitLoss = async () => {
    if (!fromDate || !toDate) {
      setAlert({
        show: true,
        message: 'Please select both from and to dates.',
        variant: 'warning',
      });
      setTimeout(
        () => setAlert({ show: false, message: '', variant: '' }),
        3000,
      );
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}profit-loss`,
        {
          from_date: fromDate,
          to_date: toDate,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status === 'success') {
        setProfitLoss(response.data.profit_loss);
      }
    } catch (err) {
      console.error('Profit & Loss Error:', err);
      setAlert({
        show: true,
        message:
          err.response?.data?.message ||
          err.message ||
          'Failed to fetch profit & loss report. Please try again.',
        variant: 'danger',
      });
      setTimeout(
        () => setAlert({ show: false, message: '', variant: '' }),
        3000,
      );
    }
    setLoading(false);
  };

  return (
    <Layout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          padding: '30px 20px',
        }}
      >
        {/* Header Card */}
        <div
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            color: 'white',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '15px',
            }}
          >
            <div>
              <h4
                style={{
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <FaChartLine style={{ fontSize: '1.8rem' }} />
                Profit & Loss
              </h4>
              <p style={{ margin: '8px 0 0 0', opacity: 0.9 }}>
                View profit and loss statement for a specific date range
              </p>
            </div>
            <Button
              variant="outline-light"
              onClick={() => navigate(-1)}
              style={{
                borderRadius: '8px',
                padding: '8px 16px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <FaArrowLeft /> Back
            </Button>
          </div>
        </div>

        {/* Alert Messages */}
        {alert.show && (
          <Alert
            variant={alert.variant}
            dismissible
            onClose={() => setAlert({ ...alert, show: false })}
            style={{
              borderRadius: '8px',
              marginBottom: '20px',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Filter Card */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <div className="row g-3">
            <div className="col-12 col-md-4">
              <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                From Date
              </Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: 'white',
                    borderRight: 'none',
                    borderRadius: '8px 0 0 8px',
                    padding: '10px 12px',
                  }}
                >
                  <FaCalendarAlt style={{ color: '#667eea' }} />
                </InputGroup.Text>
                <Form.Control
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={{
                    borderLeft: 'none',
                    borderRadius: '0 8px 8px 0',
                    paddingLeft: '0',
                    height: '42px',
                  }}
                />
              </InputGroup>
            </div>
            <div className="col-12 col-md-4">
              <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                To Date
              </Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: 'white',
                    borderRight: 'none',
                    borderRadius: '8px 0 0 8px',
                    padding: '10px 12px',
                  }}
                >
                  <FaCalendarAlt style={{ color: '#667eea' }} />
                </InputGroup.Text>
                <Form.Control
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={{
                    borderLeft: 'none',
                    borderRadius: '0 8px 8px 0',
                    paddingLeft: '0',
                    height: '42px',
                  }}
                />
              </InputGroup>
            </div>
            <div className="col-12 col-md-4">
              <Form.Label style={{ fontWeight: '600', color: 'transparent' }}>
                Action
              </Form.Label>
              <Button
                onClick={fetchProfitLoss}
                disabled={loading}
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  height: '42px',
                  fontWeight: '600',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" />
                    Loading...
                  </>
                ) : (
                  <>
                    <FaSearch /> Get Report
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Profit & Loss Report Cards */}
        {profitLoss && (
          <div className="row g-4">
            {/* Total Income Card */}
            <div className="col-12 col-md-4">
              <Card
                style={{
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  height: '100%',
                }}
              >
                <Card.Body style={{ padding: '30px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '15px',
                      marginBottom: '20px',
                    }}
                  >
                    <div
                      style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <FaArrowUp style={{ fontSize: '1.5rem' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>
                        Total Income
                      </div>
                      <div
                        style={{
                          fontSize: '1.8rem',
                          fontWeight: '700',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                        }}
                      >
                        <FaRupeeSign style={{ fontSize: '1.5rem' }} />
                        {parseFloat(
                          profitLoss.total_income || 0,
                        ).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </div>

            {/* Total Expense Card */}
            <div className="col-12 col-md-4">
              <Card
                style={{
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  background:
                    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white',
                  height: '100%',
                }}
              >
                <Card.Body style={{ padding: '30px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '15px',
                      marginBottom: '20px',
                    }}
                  >
                    <div
                      style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <FaArrowDown style={{ fontSize: '1.5rem' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>
                        Total Expense
                      </div>
                      <div
                        style={{
                          fontSize: '1.8rem',
                          fontWeight: '700',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                        }}
                      >
                        <FaRupeeSign style={{ fontSize: '1.5rem' }} />
                        {parseFloat(
                          profitLoss.total_expense || 0,
                        ).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </div>

            {/* Net Profit/Loss Card */}
            <div className="col-12 col-md-4">
              <Card
                style={{
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  background:
                    profitLoss.net_profit >= 0
                      ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                      : 'linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)',
                  color: 'white',
                  height: '100%',
                }}
              >
                <Card.Body style={{ padding: '30px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '15px',
                      marginBottom: '20px',
                    }}
                  >
                    <div
                      style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <FaChartLine style={{ fontSize: '1.5rem' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>
                        {profitLoss.net_profit >= 0 ? 'Net Profit' : 'Net Loss'}
                      </div>
                      <div
                        style={{
                          fontSize: '1.8rem',
                          fontWeight: '700',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                        }}
                      >
                        <FaRupeeSign style={{ fontSize: '1.5rem' }} />
                        {Math.abs(profitLoss.net_profit || 0).toLocaleString(
                          'en-IN',
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProfitLoss;
